<template>
    <div>
        <h3 class="text-xl sm:text-2xl xl:text-3xl mb-6">Claim your perfect domain</h3>

        <div class="flex flex-wrap">
            <div class="w-full pb-8 md:w-2/3 md:pr-4">
                <!-- Lookup a domain's availability. -->
                <sau-domain-lookup :action="domainLookup" :searching="searching" :error="error"></sau-domain-lookup>

                <!-- List TLD pricings. -->
                <div class="w-full text-center flex flex-wrap border-l">
                    <div v-for="(ext, index) in highlightedExtensions" :key="index" class="bg-white w-1/2 lg:w-1/4 py-4 text-xl border-r border-b" style="box-sizing: border-box;">
                        <p class="mb-0">
                            {{ ext.extension }}
                        </p>
                        <p class="text-green mb-0">
                            ${{ ext.pricing[userCurrency][ext.lowestYearQuantity] }}/{{ ext.lowestYearQuantity > 1 ? ext.lowestYearQuantity : '' }}yr
                        </p>
                    </div>
                </div>

                <!-- Is the searched domain available? -->
                <h3 v-if="available != null" class="md:my-8 text-xl sm:text-2xl xl:text-3xl">
                    <template v-if="available">
                        {{ searchedDomain }} is available <i class="ml-3 fas fa-check text-success"></i>
                        <br><span class="text-primary">Protect your brand - claim these too!</span>
                    </template>
                    <template v-else>
                        {{ searchedDomain }} is unavailable <i class="ml-3 fas fa-times text-danger"></i>
                    </template>
                </h3>

                <!-- Choose domains for the cart. -->
                <div class="suggestions bg-white" v-if="suggestions.length">

                    <sau-dynamic-table :data="suggestions" :collapse="true">
                        <template #table-header>
                            <tr>
                                <th>Domain</th>
                                <th>Registration Period</th>
                                <th>Add</th>
                            </tr>
                        </template>

                        <template v-slot="{ item }">
                            <tr>
                                <td>{{ item.domain }}</td>
                                <td>
                                    <select class="min-width" @change="updateRegistrationPeriod(item)" v-model="item.registration_period">
                                        <option
                                            v-for="period in item.price"
                                            :value="period.registration_period"
                                            :key="period.domain">{{ period.display }} - {{ period.rrp | currency }} (Ex. GST)
                                        </option>
                                    </select>
                                </td>
                                <td @click="actionDomain(item)">
                                    <i class="fas fa-cart-plus text-xl" :class="{ 'text-grey' : ! item.selected, 'text-success' : item.selected }"></i>
                                </td>
                            </tr>
                        </template>
                    </sau-dynamic-table>

                </div>
            </div>

            <sau-ordering-sidebar :data="data" :order="order" :processing="false"></sau-ordering-sidebar>

        </div>
    </div>
</template>

<script>

import SauDomainLookup from './DomainLookup';
import SauTable from "../utilities/SauTable";

export default {

    props: {
        data: Object,
        order: Object,
    },

    components: {
        'sau-domain-lookup': SauDomainLookup,
        'sau-dynamic-table': SauTable,
    },


    data: function () {

        return {
            domain: '',
            searchedDomain: '',
            available: null,
            suggestions : [],
            error: '',
            searching: false,
            type: 'domain',
            listeners: {},
            priceList: null,
        };
    },

    computed: {
        highlightedExtensions: function () {
            return this.data.config.domain.length
                ? this.data.config.domain.filter(ext => ext.highlighted).map(ext => {
                    ext.lowestYearQuantity = Object.keys(ext.pricing).find(key => ext.pricing[key] > 0);
                    return ext;
                })
                : [];
        },

    },

    mounted: function () {
        window.bus.$on('remove-domain', (domain) => {
            this.removeDomain(domain);
        });
        window.bus.$on('clear-domain-suggestions', () => {
            this.clearSuggestions();
        });

        this.priceList = this.data.config.domain.domainregister;
    },

    methods: {
        domainLookup(domain) {

            this.domain = domain;
            this.error = '';
            this.searching = true;
            window.bus.$emit('paginator-reset');

            this.checkDomainAvailability({domain: this.domain})
                .then(response => {

                    this.searchedDomain = this.domain;
                    this.available = response.data.available;

                    this.suggestions = [{ domain: this.domain, available: response.data.available }]
                        .concat(response.data.suggestions)
                        .filter(suggestion => this.isExtensionSupported(suggestion.domain))
                        .filter(suggestion => suggestion.available) // make sure it's available
                        .map(suggestion => {
                            return {...suggestion, ...{
                                selected: this.isDomainInOrder(suggestion.domain),
                                extension: this.getDomainExtension(suggestion.domain),
                                price: this.getDomainPricing(suggestion.domain),
                                registration_period: this.getDomainPricing(suggestion.domain)[0].registration_period
                            }};

                        })
                })
                .catch(error => {

                    this.clearSuggestions();
                    this.searchedDomain = this.domain;
                    error.response = error.response ? error.response : {};

                    switch (error.response.status) {
                        case 404: // Not found.
                            this.error = error.response.data.error;
                            break;
                        case 422: // Unprocessable Entity. (validation error)
                            this.error = error.response.data.errors.domain[0];
                            break;
                        default:
                            this.error = 'Something went wrong. Please try again.';
                    }
                })
                .then(response => {
                    this.searching = false
                });
        },

        isDomainInOrder(domain) {
            return this.order.items.find(
                item => item.type === this.type && item.domain === domain
            );
        },

        getDomainExtension(domain) {
            return `.${domain.split('.').slice(1).join('.')}`;
        },

        isExtensionSupported(domain) {
            const extension = this.getDomainExtension(domain);
            return this.priceList.find(config => config.extension === extension);
        },

        getDomainPricing(domain) {
            const extension = this.getDomainExtension(domain);
            const domainPricing = this.priceList.find(extPricing => extPricing.extension === extension);
            return domainPricing.pricing;
        },

        removeDomain: function (domainToRemove) {

            let isSuggested = true;

            if (typeof domainToRemove === 'string' || domainToRemove instanceof String) {
                let foundDomain;

                foundDomain = this.suggestions.find(suggestion => suggestion.domain === domainToRemove);

                if (! foundDomain) {
                    isSuggested = false;
                    foundDomain = this.order.items.find(item => item.type === this.type && item.domain === domainToRemove);
                }

                domainToRemove = foundDomain;
            }

            if (! domainToRemove) {
                return;
            }

            let itemToRemove = this.order.items.find(
                item => item.type === this.type && item.domain === domainToRemove.domain
            );

            if (! itemToRemove) {
                domainToRemove.selected = false;
                return;
            }

            if (itemToRemove.slug.startsWith(`temp-${this.type}-`)) {
                return;
            }

            if (isSuggested) {
                domainToRemove.selected = false;
            }

            if (! itemToRemove) {
                return;
            }

            window.bus.$emit('order.items.delete', itemToRemove);
        },

        addDomain: function (suggestion) {
            suggestion.selected = true;

            if (this.isDomainUniqueToOrder(suggestion.domain) === false) {
                return;
            };

            if (! suggestion.registration_period) {
                return;
            }

            let listenerKey = `order.items.created-${suggestion.domain}-${suggestion.registration_period}`;

            let itemData = {
                createKey: listenerKey,
                domain: suggestion.domain,
                registration_period: suggestion.registration_period,
            };

            this.listeners[listenerKey] = response => {
                this.$nextTick(() => {
                    let item = this.order.items.find(item => {
                        return item.createKey == listenerKey;
                    });
                    if (item && item.slug === response.data.slug) {
                        delete itemData.createKey;
                        window.bus.$off('order.items.created', this.listeners[listenerKey]);
                        window.bus.$emit('order.items.update', this.type, item.slug, itemData);
                    }
                })
            };

            window.bus.$on('order.items.created', this.listeners[listenerKey]);

            window.bus.$emit('order.items.create', this.type, {
                price: {
                    rrp: 0,
                    discount: 0,
                    total: suggestion.price.find(option => option.registration_period == suggestion.registration_period).price
                },
                ...itemData,
            });
        },

        isDomainUniqueToOrder: function (domain) {
            let domainIsInOrder = this.order.items.filter(
                item => item.type === this.type && item.domain === domain
            ).length;

            if (domainIsInOrder) {
                this.$swal({
                    timer: 2500,
                    toast: true,
                    type: 'info',
                    showConfirmButton: false,
                    title: `You've already added that domain to your cart.`,
                });

                return false;
            }

            return true;
        },

        updateRegistrationPeriod: function (itemToUpdate) {
            let domain = this.fetchDomainFromOrder(itemToUpdate.domain);

            if(! domain) return; // The domain is not in the order

            window.bus.$emit('order.items.update', this.type, domain.slug, {
                registration_period: itemToUpdate.registration_period,
            });
        },

        fetchDomainFromOrder(domain) {
            return this.order.items.find(item => item.domain === domain);
        },

        fetchItemFromOrder(slug) {
            return this.order.items.find(item => item.slug === slug);
        },

        clearSuggestions: function () {
            this.available = null;
            this.suggestions = [];
        },
        actionDomain(item) {
            item.selected ? this.removeDomain(item) : this.addDomain(item);
        },
    }
}
</script>

<style scoped="scoped" lang="scss">

    @media (min-width: 575px) {
        .suggestions select.min-width {
            min-width: 165px;
        }
    }

    .suggestion {
        &:hover {
            box-shadow: 0 0 2px theme('colors.blue');
        }
        &.selected, &:hover {
            &, * {
                color: theme('colors.blue') !important;
                border-color: theme('colors.blue') !important;
            }
        }
    }

    .pricing-select {
        border: none;
        text-align: center;
        text-align-last: center;
        padding-top: 5px !important;
        &:focus, &:hover {
            border: none;
            outline: none;
            box-shadow: none;
        }
    }

    // --breakpoint-lg
    @media (min-width: 992px) {
        .domain-banner {
            background-image: url('/img/apiro-claim-banner.png');
            background-size: cover;
            background-repeat: no-repeat;
            height: 265px;
        }
    }

    // Animation
    .animated {
      animation-duration: 1s;
      animation-fill-mode: both;
    }
    @-webkit-keyframes shake {
      from, to { transform: translate3d(0, 0, 0); }
      10%, 30%, 50%, 70%, 90% { transform: translate3d(-10px, 0, 0); }
      20%, 40%, 60%, 80% { transform: translate3d(10px, 0, 0); }
    }
    @keyframes shake {
        from, to { transform: translate3d(0, 0, 0); }
        10%, 30%, 50%, 70%, 90% { transform: translate3d(-10px, 0, 0); }
        20%, 40%, 60%, 80% { transform: translate3d(10px, 0, 0); }
    }
    .shake { animation-name: shake; }
</style>
